import type {
  BillingContractAmendmentStatus,
  BillingContractAmendmentType,
  BillingContractStatus,
  BillingFrequency,
} from '../generated/enums.js';
import type { ExternalMetadata } from './bills.js';
import type {
  ExternalCreateInvoiceWithholding,
  ExternalInvoicePaymentMethod,
  ExternalInvoicePaymentStatus,
  ExternalInvoiceResponse,
} from './invoices.js';

/**
 * WorkOS org API key scopes for the External API billing-contract surface
 * (ALC-2234). Same auth plane as `ExternalInvoiceScope` / `ExternalBillScope`:
 * checked by `RequireExternalScope` against `req.externalContext.permissions`,
 * never Platform JWT RBAC slugs (see docs/agents/identity-decisions.md § ID-024).
 */
export const ExternalContractScope = {
  READ: 'external:contracts:read',
  WRITE: 'external:contracts:write',
} as const;
export type ExternalContractScope =
  (typeof ExternalContractScope)[keyof typeof ExternalContractScope];

/** All External API contract scopes (WorkOS org API key allowlist + route guards). */
export const EXTERNAL_API_CONTRACT_SCOPES = [
  ExternalContractScope.READ,
  ExternalContractScope.WRITE,
] as const satisfies readonly ExternalContractScope[];

/**
 * Maximum length of a contract `externalReferenceId` on the External API.
 *
 * ALC-2234 set this to 20 because the reference was persisted verbatim into
 * `BillingContract.code` (`@db.VarChar(20)`), the only tenant-unique
 * caller-supplyable key the aggregate exposed, and that slice added no schema.
 * Its own doc called the ceiling inherited, not chosen.
 *
 * ALC-2235 — the designated schema slice — adds
 * `BillingContract.externalReferenceId @db.VarChar(255)` with its own tenant
 * unique index, so the reference no longer has to fit in `code`. The ceiling is
 * now 255, which comfortably holds a UUID-shaped subscription id. Refs longer
 * than that are still rejected at the DTO boundary with
 * `CONTRACT_EXTERNAL_REFERENCE_TOO_LONG` — a loud per-row failure at import
 * time, never a silent truncation that would break the `by-reference` lookups
 * and ALC-2235's `contractReferenceId` invoice join.
 */
export const EXTERNAL_CONTRACT_REFERENCE_MAX_LENGTH = 255;

/**
 * Maximum length of `BillingContract.code` (`@db.VarChar(20)`).
 *
 * A reference at or under this is still used verbatim as the contract code, so
 * every contract created before ALC-2235 — and every short reference after it —
 * keeps exactly the code it had. Longer references get a derived code instead
 * (see `deriveContractCodeFromReference`); the reference itself always lands in
 * `externalReferenceId` regardless.
 */
export const BILLING_CONTRACT_CODE_MAX_LENGTH = 20;

export interface ExternalContractItemRequest {
  /** Billing service identifier (UUID) — mutually exclusive with `serviceCode`. */
  serviceId?: string;
  /** Billing service catalog code — resolved to a service in the caller's tenant. */
  serviceCode?: string;
  /** Price this person actually pays after any introductory period. Per-person pricing;
   * no catalog price is enforced (`BillingService.floorPrice` still applies when set). */
  regularPrice: number;
  /** Fixed introductory price, including zero. */
  introductoryPrice?: number | null;
  /** Monthly billing periods charged at `introductoryPrice`. */
  introductoryMonths?: number | null;
}

/**
 * One elapsed cycle's charge, delivered INSIDE `POST /v1/contracts` (ALC-2823).
 *
 * Deliberately a narrowed `ExternalCreateInvoiceRequest`, not the whole thing: `entityId`,
 * the customer identifiers and `contractReferenceId` are all facts the surrounding contract
 * payload already states, and letting an inline entry restate them would create three ways
 * for one request to disagree with itself — a charge bound to a different customer than the
 * subscription it is being created with is exactly the corruption
 * `CUSTOMER_IDENTIFIERS_CONFLICT` exists to refuse, and here it does not even need to be
 * refused because it cannot be expressed. Every other field means precisely what it means
 * on `POST /v1/invoices`.
 */
export interface ExternalContractInlineInvoiceRequest {
  /** Integrator idempotency key for THIS charge — deduplicated exactly as on
   * `POST /v1/invoices`, so a replay of the whole contract call never duplicates it. */
  externalReferenceId: string;
  issueDate: string;
  /** Gross invoice amount, decimal string. */
  grossAmount: string;
  /** Net collectible amount, decimal string. */
  netAmount: string;
  invoiceNumber?: string;
  dueDate?: string;
  description?: string;
  /** Billing service catalog code. Required on a charge that names a `billingPeriodStart`
   * against a deferring contract item, or the charge is refused with
   * `CONTRACT_SERVICE_NOT_ON_CONTRACT`. */
  serviceCode?: string;
  /**
   * The elapsed cycle this charge bills, EXACTLY a contract occurrence boundary. Together
   * the marked entries must cover every occurrence already due at the moment of the call —
   * an uncovered one is `RETROACTIVE_REQUIRES_INVOICES`. Omit it on an entry that is not the
   * full recurring charge for a cycle (an installment of a "parcelamento sem limite", an
   * add-on), exactly as on `POST /v1/invoices`.
   */
  billingPeriodStart?: string;
  withholdings?: ExternalCreateInvoiceWithholding[];
  paymentStatus?: ExternalInvoicePaymentStatus;
  /** Required with `paymentStatus: PAID` — provenance for when the charge was captured. */
  paidAt?: string;
  paymentMethod?: ExternalInvoicePaymentMethod;
  /**
   * The gateway/payment reference. Required whenever `paymentStatus` is `PAID` — a charge
   * settled with no reconcilable reference would sit in aging and in the cash forecast
   * forever, because bank matching joins on exactly this value and `PAID_UNCONFIRMED` is
   * projected as a future inflow (ALC-2823, accounting gate CFO-02).
   *
   * For history whose payment genuinely cannot be referenced, omit `paymentStatus` instead:
   * the charge is imported, issued and recognized normally, and is settled by linking its
   * NF/receipt in Alchemy — which takes it out of AR and the forecast the way every other
   * settlement does.
   */
  externalPaymentRefId?: string;
  metadata?: ExternalMetadata;
}

export interface ExternalCreateContractRequest {
  /** The source system's subscription ID. Idempotency anchor and lookup key. */
  externalReferenceId: string;
  entityId: string;
  /** AR customer identifier (UUID) — one of customerId/customerCode/customerTaxId. */
  customerId?: string;
  customerCode?: string;
  /** CNPJ or CPF. */
  customerTaxId?: string;
  /** ISO date. May be backdated — the bulk import of already-active subscriptions
   * runs through this route (see `ExternalContractResponse.warnings`). */
  startDate: string;
  /**
   * Required unless `templateCode` is set, in which case it defaults to the template's
   * `defaultDurationMonths` — supply it only when the template carries no default, or to
   * override it for this one contract.
   */
  termDurationMonths?: number;
  /**
   * When set, resolved to a `BillingContractTemplate` (ALC-2820) and the contract copies
   * down its renewal terms, cadence, payment method/installments and item pricing
   * defaults byte-for-byte — the SAME internal from-template path the Platform UI uses,
   * never a re-derivation. `billingFrequency`, `autoRenewal` and `currency` are then
   * template-owned and MUST be omitted here (send `VALIDATION_ERROR` if present); `items`
   * becomes optional PRICE overrides (see below) instead of the full item list.
   * Inactivated templates reject new contract creation with `TEMPLATE_INACTIVE`;
   * existing contracts already created from them are unaffected. Unknown code raises
   * `TEMPLATE_NOT_FOUND`.
   */
  templateCode?: string;
  autoRenewal?: boolean;
  billingFrequency?: BillingFrequency;
  currency?: string;
  /**
   * Date from which billing occurrences and revenue schedules are generated. Defaults to
   * the activation instant, so a backdated `startDate` records the true commercial start
   * WITHOUT materializing retroactive AR and recognition history — for Casa do Saber that
   * history arrives through the NFS-e backfill workstream, and generating it here would
   * double-count it. Set explicitly (ISO date) only when the retroactive periods really
   * should be generated.
   */
  automationStartDate?: string | null;
  /**
   * Required when `templateCode` is omitted (the full item list). Optional PRICE
   * overrides when `templateCode` is set — "price is per person" (see the integration
   * guide): each entry must name a service the template already carries and MAY set
   * `regularPrice` only. `introductoryPrice`/`introductoryMonths` are template-owned in
   * that mode and rejected with `VALIDATION_ERROR`. Omitted entirely, the contract bills
   * every template item at the template's own default price.
   */
  items?: ExternalContractItemRequest[];
  /** Flat integrator metadata (ALC-2236) — e.g. `{ coupon: "BLACKFRIDAY2026",
   * salesChannel: "web", offerType: "annual" }`. On a replay (same externalReferenceId
   * as an existing contract), omitting this field leaves stored metadata untouched.
   * `metadata: null` is rejected with `METADATA_INVALID` — omit the field instead. */
  metadata?: ExternalMetadata;
  /**
   * The elapsed cycles' charges, for a RETROACTIVE create (ALC-2823).
   *
   * REQUIRED whenever `startDate` is in the past — a retroactive create without them is
   * `RETROACTIVE_REQUIRES_INVOICES` (422). REFUSED with `VALIDATION_ERROR` when `startDate`
   * is today or later: a contract that has not yet run a cycle has no history to carry, and
   * its first charge goes through `POST /v1/invoices` like every other one.
   *
   * See `ExternalContractInlineInvoiceRequest` for the shape and
   * `RETROACTIVE_REQUIRES_INVOICES` for the coverage rule.
   */
  invoices?: ExternalContractInlineInvoiceRequest[];
}

/**
 * Corroborating customer identity required by every `by-reference/:ref` MUTATION.
 *
 * The reference resolves to `BillingContract.code`, and contracts created in the UI get
 * guessable sequential codes (`CTR-001`). Without a second factor an integrator key
 * could cancel or reprice an internal contract by guessing its code. A mismatch is
 * reported as `CONTRACT_NOT_FOUND`, never as a distinct "wrong customer" error — telling
 * a caller that a code exists but belongs to someone else is itself the leak.
 * Mirrors the customers slice's tax-id corroboration (ALC-2233).
 */
export interface ExternalContractCustomerCorroboration {
  /** The customer's integrator-side reference (`ArCustomer.code`). */
  customerReferenceId?: string;
  /** CPF or CNPJ. */
  customerTaxId?: string;
}

export interface ExternalUpdateContractRenewalRequest extends ExternalContractCustomerCorroboration {
  autoRenewal: boolean;
  /** Months the contract renews FOR. Distinct from `termDurationMonths`. */
  renewalDurationMonths?: number | null;
}

export interface ExternalCancelContractRequest extends ExternalContractCustomerCorroboration {
  /** Recorded on the request log; not persisted on the contract (no reason column). */
  reason: string;
  /** ISO date. Must be today or omitted — the internal transition always stamps "now",
   * and accepting a date the flow then ignores would misdate churn. */
  effectiveDate?: string;
}

/**
 * Extends the SAME contract by one renewal term through the internal RENEWAL amendment
 * machinery — never a new `BillingContract` (metrics live on the contract row) (ALC-2825).
 *
 * CDS runs `autoRenewal: false`, so nothing internal ever extends the term on its own;
 * renewal is caller-triggered, normally right after the integrator's own charge succeeds.
 *
 * `renewalTermStartDate` is BOTH the term boundary and the idempotency key — it is never
 * derived from `currentTermEndDate` server-side, because that column moves the instant a
 * renewal commits, and a server-derived key would silently renew AGAIN on a naive retry
 * that re-read the (now advanced) column. See `ExternalApiErrorCode.CONTRACT_ALREADY_RENEWED`
 * / `CONTRACT_RENEWAL_TERM_MISMATCH` for the exact replay/refusal rule.
 */
export interface ExternalRenewContractRequest extends ExternalContractCustomerCorroboration {
  /**
   * The new term's start date. For a FRESH renewal this must equal the contract's own
   * `currentTermEndDate + 1 day` — sent explicitly by the caller rather than computed here,
   * so a retry always names the exact term it already asked for. For a REPLAY, this is
   * matched against an existing RENEWAL amendment's own `renewalTermStartDate` regardless of
   * where the contract's term has since moved to.
   */
  renewalTermStartDate: string;
  /** Recorded on the amendment's `reason` / the request log. Not part of the replay key —
   * a different reason on an otherwise identical retry must not conflict. */
  reason?: string;
}

export interface ExternalContractPriceChangeRequest extends ExternalContractCustomerCorroboration {
  reason: string;
  /** ISO date the resulting Version becomes effective. Must be strictly later than
   * the contract's current Version `effectiveFrom`. */
  effectiveDate: string;
  /** Full replacement item list, mirroring the internal amendment contract. Introductory
   * terms are NOT accepted here — an amendment item carries one price, so accepting them
   * would mean billing the regular price through a window the caller thought discounted. */
  items: ExternalContractItemRequest[];
  /** Required when `effectiveDate` is in the past. */
  confirmBackdatedEffectiveDate?: boolean;
}

export interface ExternalContractItemResponse {
  serviceId: string;
  serviceCode: string | null;
  regularPrice: string;
}

export interface ExternalContractResponse {
  id: string;
  externalReferenceId: string;
  status: BillingContractStatus;
  entityId: string;
  customerId: string;
  currency: string;
  startDate: string | null;
  termDurationMonths: number | null;
  currentTermEndDate: string | null;
  nextRenewalDate: string | null;
  autoRenewal: boolean;
  renewalDurationMonths: number | null;
  billingFrequency: BillingFrequency;
  items: ExternalContractItemResponse[];
  createdAt: string;
  /** Echoed back verbatim (ALC-2236). Undefined/null when the contract carries none. */
  metadata?: ExternalMetadata | null;
  /** Non-fatal notes from the internal lifecycle flow (e.g. a backdated activation's
   * missed billing/recognition periods). Never a rejection — the internal flow's own
   * refusals surface as errors. */
  warnings?: ExternalContractWarning[];
  /**
   * The charges created from `invoices[]`, in payload order (ALC-2823). Present only on a
   * retroactive create/replay that carried them.
   *
   * Echoed in full rather than as a count: the caller needs each charge's resulting
   * `status` to know whether it landed `PAID_UNCONFIRMED`, `ISSUED` or `DRAFT`, and its
   * `id` to address it later — and on a replay these are the invoices that ALREADY existed,
   * so the same field answers "what does Alchemy currently hold for this subscription's
   * history" without a second round of `GET /v1/invoices/by-reference/:ref` calls.
   */
  invoices?: ExternalInvoiceResponse[];
  /**
   * The cutover actually recorded on the contract, echoed so a retroactive caller can see
   * that automation resumes at the first cycle their `invoices[]` did NOT cover
   * (ALC-2823) — the value that keeps the daily recurring run from re-billing the history
   * they just supplied. Null when the contract has none.
   */
  automationStartDate?: string | null;
}

export interface ExternalContractWarning {
  code: string;
  message: string;
}

/**
 * A price change lands as a PENDING amendment, not an applied price.
 *
 * `BillingContractAmendmentService.approve()` enforces separation of duties —
 * the actor who proposed an amendment must not commit it. The External API has
 * exactly one actor (the tenant automation user), so approving here would mean
 * either a workflow skip or two synthetic "reviewers", which is zero reviewers.
 * The route therefore wraps `create()` only; a human approves in the UI, and the
 * churn/downsell amendment record exists from the moment of the API call.
 */
export interface ExternalContractPriceChangeResponse {
  amendmentId: string;
  contractId: string;
  externalReferenceId: string;
  amendmentType: BillingContractAmendmentType;
  status: BillingContractAmendmentStatus;
  effectiveDate: string;
  /** True while the amendment awaits internal approval — the contract still bills
   * at the previous price until then. */
  awaitingApproval: boolean;
}
